// src/components/GroupsModule.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import SkeletonLoader from './SkeletonLoader';
import { FaLayerGroup, FaEdit, FaTrash, FaPlus, FaUsers, FaBuilding, FaUserCog, FaArrowRight, FaArrowLeft, FaTimes, FaFilter } from 'react-icons/fa';

const GroupsModule = () => {
    const { auth } = useContext(AuthContext);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modale CREA/EDIT Gruppo
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: '', target_tenant_id: '' });
    
    // Modale MEMBRI
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [availableUsers, setAvailableUsers] = useState([]); // Utenti fuori dal gruppo
    const [groupMembers, setGroupMembers] = useState([]);     // Utenti nel gruppo
    
    // Filtri Membri
    const [memberSearch, setMemberSearch] = useState('');     
    const [memberStatusFilter, setMemberStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

    const [message, setMessage] = useState({ text: '', type: '' });
    const [tenants, setTenants] = useState([]);
    const [actionGroup, setActionGroup] = useState(null); // Menu mobile

    // --- FETCH GRUPPI ---
    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            let url = `/api_groups.php?role=${auth.role}`;
            if (auth.role !== 'superadmin') {
                url += `&tenant_id=${auth.tenant_id}`;
            }
            const response = await axios.get(url);
            if (response.data.success) setGroups(response.data.data);
        } catch (error) { console.error("Errore fetch gruppi", error); } 
        finally { setLoading(false); }
    }, [auth]);

    // Carica Tenants (Solo Superadmin)
    useEffect(() => {
        if (showModal && auth.role === 'superadmin' && tenants.length === 0) {
            axios.get(`/api_tenants.php?role=${auth.role}`).then(res => {
                if(res.data.success) setTenants(res.data.data);
            });
        }
    }, [showModal, auth.role, tenants.length]);

    useEffect(() => { fetchGroups(); }, [fetchGroups]);

    // --- GESTIONE GRUPPI (CRUD) ---
    const openModal = (group = null) => {
        setActionGroup(null);
        setMessage({ text: '', type: '' });
        if (group) {
            setFormData({ id: group.id, name: group.name, target_tenant_id: group.tenant_id });
        } else {
            setFormData({ id: null, name: '', target_tenant_id: auth.role === 'superadmin' ? '' : auth.tenant_id });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ text: 'Salvataggio...', type: 'info' });
        try {
            const method = formData.id ? 'put' : 'post';
            const payload = { ...formData, role: auth.role, tenant_id: auth.tenant_id };
            const response = await axios[method]('/api_groups.php', JSON.stringify(payload));
            if (response.data.success) {
                setMessage({ text: 'Successo!', type: 'success' });
                fetchGroups();
                setTimeout(() => setShowModal(false), 1000);
            } else { setMessage({ text: response.data.message, type: 'danger' }); }
        } catch (error) { setMessage({ text: 'Errore.', type: 'danger' }); }
    };

    const handleDelete = async (id) => {
        setActionGroup(null);
        if (!window.confirm("Eliminare gruppo?")) return;
        try {
            await axios.delete(`/api_groups.php?id=${id}&role=${auth.role}`);
            fetchGroups();
        } catch (e) { alert("Errore"); }
    };

    // --- GESTIONE MEMBRI (LOGICA SHUTTLE) ---
    const openMembersModal = async (group) => {
        setActionGroup(null);
        setCurrentGroup(group);
        setAvailableUsers([]);
        setGroupMembers([]);
        setMemberSearch('');
        setMemberStatusFilter('all'); // Reset filtro
        setShowMembersModal(true);

        try {
            // Recupera Utenti e Membri attuali
            const res = await axios.get(`/api_group_members.php?group_id=${group.id}&tenant_id=${group.tenant_id}`);
            if (res.data.success) {
                const all = res.data.all_users; // Deve contenere {id, name, email, is_active}
                const memberIds = res.data.member_ids; 

                const members = all.filter(u => memberIds.includes(u.id));
                const available = all.filter(u => !memberIds.includes(u.id));

                setGroupMembers(members);
                setAvailableUsers(available);
            }
        } catch (e) { alert("Errore caricamento membri"); setShowMembersModal(false); }
    };

    const moveUser = (user, direction) => {
        if (direction === 'toGroup') {
            setAvailableUsers(availableUsers.filter(u => u.id !== user.id));
            setGroupMembers([...groupMembers, user]);
        } else {
            setGroupMembers(groupMembers.filter(u => u.id !== user.id));
            setAvailableUsers([...availableUsers, user]);
        }
    };

    const saveMembers = async () => {
        try {
            const payload = {
                group_id: currentGroup.id,
                user_ids: groupMembers.map(u => u.id)
            };
            const res = await axios.post('/api_group_members.php', JSON.stringify(payload));
            if (res.data.success) {
                alert("Membri aggiornati!");
                setShowMembersModal(false);
                fetchGroups();
            }
        } catch (e) { alert("Errore salvataggio"); }
    };

    // Filtro combinato (Search + Status)
    const filterList = (list) => {
        return list.filter(u => {
            const matchesSearch = u.name.toLowerCase().includes(memberSearch.toLowerCase()) || 
                                  u.email.toLowerCase().includes(memberSearch.toLowerCase());
            
            let matchesStatus = true;
            if (memberStatusFilter === 'active') matchesStatus = (u.is_active == 1);
            if (memberStatusFilter === 'inactive') matchesStatus = (u.is_active == 0);

            return matchesSearch && matchesStatus;
        });
    };

    const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="container-fluid fade-in">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="text-primary fw-bold m-0">Gestione Gruppi</h4>
                <button className="btn btn-success btn-sm shadow-sm" onClick={() => openModal()}>
                    <FaPlus className="me-2" /> Nuovo Gruppo
                </button>
            </div>

            <div className="card shadow-sm mb-3 border-0">
                <div className="card-body p-2">
                    <input type="text" className="form-control form-control-sm border-0" placeholder="Cerca gruppo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
            </div>

            {/* --- TABLE DESKTOP --- */}
            <div className="card shadow-sm border-0 d-none d-md-block">
                <div className="card-body p-0">
                    <table className="table table-hover align-middle mb-0" style={{fontSize: '0.9rem'}}>
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4">Nome Gruppo</th>
                                <th>Azienda</th>
                                <th className="text-center">Membri</th>
                                <th className="text-end pe-4">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? ( [...Array(3)].map((_, i) => <tr key={i}><td colSpan="4" className="p-3"><SkeletonLoader /></td></tr>) ) : 
                            filteredGroups.length > 0 ? (
                                filteredGroups.map(g => (
                                    <tr key={g.id}>
                                        <td className="ps-4 fw-bold text-primary">{g.name}</td>
                                        <td>
                                            <div className="d-flex align-items-center text-muted">
                                                <FaBuilding className="me-2 opacity-50" /> {g.tenant_name || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-light text-dark border">{g.user_count} <FaUsers className="ms-1 text-muted" /></span>
                                        </td>
                                        <td className="text-end pe-4">
                                            <button className="btn btn-sm btn-outline-info me-2" onClick={() => openMembersModal(g)} title="Gestisci Membri"><FaUserCog /></button>
                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openModal(g)}><FaEdit /></button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(g.id)}><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))
                            ) : ( <tr><td colSpan="4" className="text-center py-4 text-muted">Nessun gruppo.</td></tr> )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- MOBILE LIST --- */}
            <div className="d-md-none">
                {loading ? ( [...Array(3)].map((_, i) => <div key={i} className="mb-2"><SkeletonLoader height="80px"/></div>) ) : 
                filteredGroups.length > 0 ? (
                    filteredGroups.map(g => (
                        <div key={g.id} className="card mb-2 shadow-sm border-0" onClick={() => setActionGroup(g)}>
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="fw-bold text-primary"><FaLayerGroup className="me-2" /> {g.name}</div>
                                    <span className="badge bg-light text-dark border">{g.user_count} Utenti</span>
                                </div>
                                <div className="text-muted small"><FaBuilding className="me-2 opacity-50" /> {g.tenant_name || 'N/A'}</div>
                            </div>
                        </div>
                    ))
                ) : ( <div className="text-center py-5 text-muted">Nessun gruppo.</div> )}
            </div>

            {/* --- MOBILE MENU --- */}
            {actionGroup && (
                <div className="modal d-block" style={{backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060}} onClick={() => setActionGroup(null)}>
                    <div className="modal-dialog modal-dialog-centered px-4">
                        <div className="modal-content shadow border-0" onClick={e => e.stopPropagation()}>
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold">{actionGroup.name}</h5>
                                <button className="btn-close" onClick={() => setActionGroup(null)}></button>
                            </div>
                            <div className="modal-body d-grid gap-2">
                                <button className="btn btn-outline-info py-2" onClick={() => openMembersModal(actionGroup)}><FaUserCog className="me-2" /> Gestisci Membri</button>
                                <button className="btn btn-outline-primary py-2" onClick={() => openModal(actionGroup)}><FaEdit className="me-2" /> Modifica Gruppo</button>
                                <button className="btn btn-outline-danger py-2" onClick={() => handleDelete(actionGroup.id)}><FaTrash className="me-2" /> Elimina Gruppo</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODALE MEMBRI (SHUTTLE + FILTRI) --- */}
            {showMembersModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title"><FaUserCog className="me-2" /> Membri: {currentGroup?.name}</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowMembersModal(false)}></button>
                            </div>
                            
                            <div className="modal-body bg-light">
                                {/* FILTRI RICERCA & STATO */}
                                <div className="row g-2 mb-3">
                                    <div className="col-md-8">
                                        <input type="text" className="form-control" placeholder="Cerca utente per nome o email..." value={memberSearch} onChange={e => setMemberSearch(e.target.value)} />
                                    </div>
                                    <div className="col-md-4">
                                        <div className="input-group">
                                            <span className="input-group-text bg-white"><FaFilter className="text-muted"/></span>
                                            <select className="form-select" value={memberStatusFilter} onChange={e => setMemberStatusFilter(e.target.value)}>
                                                <option value="all">Tutti gli stati</option>
                                                <option value="active">Solo Attivi</option>
                                                <option value="inactive">Solo Sospesi</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="row g-2">
                                    {/* LISTA DISPONIBILI */}
                                    <div className="col-md-6">
                                        <div className="card h-100 shadow-sm">
                                            <div className="card-header bg-white fw-bold small text-muted">Disponibili</div>
                                            <div className="list-group list-group-flush overflow-auto" style={{height: '300px'}}>
                                                {filterList(availableUsers).map(u => (
                                                    <button key={u.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => moveUser(u, 'toGroup')}>
                                                        <div className="text-truncate">
                                                            {u.name} 
                                                            <span className={`badge ms-2 ${u.is_active ? 'bg-success' : 'bg-danger'} rounded-pill`} style={{fontSize:'0.6em'}}>{u.is_active ? 'Attivo' : 'Off'}</span>
                                                        </div>
                                                        <FaArrowRight className="text-primary small" />
                                                    </button>
                                                ))}
                                                {filterList(availableUsers).length === 0 && <div className="p-3 text-center text-muted small">Nessuno trovato</div>}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* LISTA MEMBRI */}
                                    <div className="col-md-6">
                                        <div className="card h-100 shadow-sm border-primary">
                                            <div className="card-header bg-primary text-white fw-bold small">Nel Gruppo ({groupMembers.length})</div>
                                            <div className="list-group list-group-flush overflow-auto" style={{height: '300px'}}>
                                                {filterList(groupMembers).map(u => (
                                                    <button key={u.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => moveUser(u, 'fromGroup')}>
                                                        <FaArrowLeft className="text-danger small" />
                                                        <div className="text-truncate text-end w-100">{u.name}</div>
                                                    </button>
                                                ))}
                                                {filterList(groupMembers).length === 0 && <div className="p-3 text-center text-muted small">Gruppo vuoto</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowMembersModal(false)}>Annulla</button>
                                <button className="btn btn-primary" onClick={saveMembers}>Salva Modifiche</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODALE CREA/EDIT GRUPPO --- */}
            {showModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">{formData.id ? 'Modifica' : 'Nuovo'} Gruppo</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {message.text && <div className={`alert alert-${message.type}`}>{message.text}</div>}
                                <form onSubmit={handleSubmit}>
                                    {auth.role === 'superadmin' && (
                                        <div className="mb-3">
                                            <label className="form-label small fw-bold">Azienda</label>
                                            <select className="form-select" value={formData.target_tenant_id} onChange={e => setFormData({...formData, target_tenant_id: e.target.value})} disabled={!!formData.id}>
                                                <option value="">Seleziona...</option>
                                                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Nome Gruppo</label>
                                        <input type="text" className="form-control" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                    </div>
                                    <div className="text-end">
                                        <button type="button" className="btn btn-secondary me-2" onClick={() => setShowModal(false)}>Annulla</button>
                                        <button type="submit" className="btn btn-primary">Salva</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupsModule;